/**
 * LE PARCOURS D'UNE NC — module PUR (RQ, 03/10/2026).
 *
 * « Le circuit n'est pas clair du tout. Chaque étape dans un onglet, et qu'on
 * avance de façon guidée. » Le dossier montrait DEUX circuits qui ne se
 * recouvraient pas : une frise d'états (Déclarée → Analyse → Traitement →
 * Clôturée) et des onglets de contenu (Description · Analyse & CAPA · Actions ·
 * Liens). Il n'y en a plus qu'un : chaque ÉTAPE est un état ET un onglet.
 *
 *   1 Constat     — soumise · déclarée    Que s'est-il passé ?
 *   2 Analyse     — en analyse            Pourquoi est-ce arrivé ?
 *   3 Traitement  — en traitement         Que fait-on pour que cela ne se reproduise pas ?
 *   4 Clôture     — clôturée              Tout est-il en ordre pour clore ?
 *
 * Aucune règle métier ici : l'ordre des états reste celui du moteur. Ce module
 * dit seulement OÙ l'on en est, ce qu'on attend à cette étape, et quelle est la
 * suivante.
 */

export type EtapeNC = 'constat' | 'analyse' | 'traitement' | 'cloture';

export interface DefEtape {
  cle: EtapeNC;
  titre: string;
  /** La question à laquelle l'étape répond. */
  question: string;
  /** Ce qu'on fait ici, en une phrase. */
  consigne: string;
  /** Les états du moteur qui correspondent à cette étape. */
  statuts: readonly string[];
}

export const ETAPES: readonly DefEtape[] = [
  {
    cle: 'constat', titre: 'Constat', question: 'Que s’est-il passé, et qui le prend en charge ?',
    consigne: 'Trois onglets, comme à la déclaration : ce qui s’est passé, ses conséquences, sa prise en charge.',
    statuts: ['soumise', 'declaree'],
  },
  {
    cle: 'analyse', titre: 'Analyse', question: 'Pourquoi est-ce arrivé ?',
    consigne: 'Remontez à la cause : dites le fait constaté, cochez les étiologies de l’Ishikawa — elles évitent d’oublier une piste — puis retenez la cause sur laquelle vous agirez.',
    statuts: ['analyse'],
  },
  {
    /* « Plan d'actions » (13/10/2026) : la correction immédiate ne s'écrit plus
       ici mais au constat — l'étape ne porte plus que l'action corrective et le
       plan. La clé `traitement` ne change pas : elle est dans les adresses. */
    cle: 'traitement', titre: 'Plan d’actions', question: 'Que fait-on pour que cela ne se reproduise pas ?',
    consigne: 'Écrivez l’action corrective qui supprime la cause, puis ouvrez les actions du plan, avec leur échéance et leur responsable.',
    statuts: ['traitement'],
  },
  {
    cle: 'cloture', titre: 'Clôture', question: 'Tout est-il en ordre pour clore ?',
    consigne: 'Le dossier se clôture quand l’action corrective est écrite et que les actions du plan sont menées.',
    statuts: ['cloturee'],
  },
];

export const NB_ETAPES = ETAPES.length;

const indexDe = (cle: EtapeNC) => ETAPES.findIndex((e) => e.cle === cle);

/** L'étape où en est le dossier. Un état inconnu (ou annulé) retombe sur le constat. */
export function etapeCourante(statut: string): EtapeNC {
  return ETAPES.find((e) => e.statuts.includes(statut))?.cle ?? 'constat';
}

export function numero(cle: EtapeNC): number {
  return indexDe(cle) + 1;
}

export function etape(cle: EtapeNC): DefEtape {
  return ETAPES[indexDe(cle)];
}

export function suivante(cle: EtapeNC): EtapeNC | null {
  return ETAPES[indexDe(cle) + 1]?.cle ?? null;
}

export function precedente(cle: EtapeNC): EtapeNC | null {
  return ETAPES[indexDe(cle) - 1]?.cle ?? null;
}

export type EtatEtape = 'faite' | 'en_cours' | 'a_venir';

/** Où en est CETTE étape par rapport à l'état du dossier. */
export function etatEtape(cle: EtapeNC, statut: string): EtatEtape {
  const courante = indexDe(etapeCourante(statut));
  const i = indexDe(cle);
  // Une NC clôturée a franchi toutes ses étapes, la dernière comprise.
  if (statut === 'cloturee') return 'faite';
  if (i < courante) return 'faite';
  return i === courante ? 'en_cours' : 'a_venir';
}

/** Ce qui manque pour dire « cette étape est bien remplie » — vide si rien. Indicatif : ne bloque rien. */
export function resteAFaire(cle: EtapeNC, nc: {
  titre?: string | null; source?: string | null; gravite?: string | null; processus?: string | null; responsable?: string | null;
  causes?: string | null; actionCorrective?: string | null;
}, actionsNonRealisees = 0): string[] {
  const manque: string[] = [];
  if (cle === 'constat') {
    if (!String(nc.source || '').trim() || nc.source === 'aqualifier') manque.push('le secteur reste « à qualifier »');
    else {
      if (!String(nc.processus || '').trim()) manque.push('le processus');
      if (!String(nc.responsable || '').trim()) manque.push('le responsable du traitement');
    }
  }
  if (cle === 'analyse' && !String(nc.causes || '').trim()) manque.push('la cause n’est pas encore analysée');
  if (cle === 'traitement') {
    if (!String(nc.actionCorrective || '').trim()) manque.push('l’action corrective n’est pas écrite');
    if (actionsNonRealisees > 0) manque.push(`${actionsNonRealisees} action(s) du plan non réalisée(s)`);
  }
  return manque;
}

/**
 * LES SIX REPÈRES DE LA FRISE (RQ, 13/10/2026).
 *
 * « Que ce soit la même interface que la déclaration, avec en plus les plans
 * d'action et la clôture. » La frise montre donc les trois sections de la
 * déclaration, puis l'analyse, le plan d'actions et la clôture.
 *
 * ⚠️ UNE COUCHE D'AFFICHAGE, PAS UN SECOND PARCOURS. Les quatre `ETAPES`
 * ci-dessus restent ce qui porte les ÉTATS du moteur et les ADRESSES
 * (`?onglet=constat|analyse|traitement|cloture` — une adresse publiée est un
 * contrat) ; les trois premiers repères pointent sur la même page, le constat,
 * chacun vers SA section (`ancre`).
 */
export interface Repere {
  numero: number;
  titre: string;
  /** L'étape du parcours (l'onglet) qui porte ce repère. */
  onglet: EtapeNC;
  /** La section de la page où descendre — pour les repères du constat. */
  ancre?: string;
}

export const REPERES: readonly Repere[] = [
  { numero: 1, titre: 'Ce qui s’est passé', onglet: 'constat', ancre: 'nc-sec-fait' },
  { numero: 2, titre: 'Conséquences', onglet: 'constat', ancre: 'nc-sec-consequences' },
  { numero: 3, titre: 'Prise en charge', onglet: 'constat', ancre: 'nc-sec-priseEnCharge' },
  { numero: 4, titre: 'Analyse', onglet: 'analyse' },
  { numero: 5, titre: 'Plan d’actions', onglet: 'traitement' },
  { numero: 6, titre: 'Clôture', onglet: 'cloture' },
];

export const NB_REPERES = REPERES.length;

/** « 1 à 3 », « 4 »… — les numéros de repère que couvre une étape du parcours. */
export function numerosAffiches(cle: EtapeNC): string {
  const n = REPERES.filter((r) => r.onglet === cle).map((r) => r.numero);
  return n.length > 1 ? `${n[0]} à ${n[n.length - 1]}` : String(n[0] ?? '');
}

